import {useCallback} from 'react';
import i18next from 'i18next';

import * as GlobalEvent from 'web-app/services/global-event';
import * as Warnable from 'web-app/util/warnable';
import * as SentryService from 'web-app/services/sentry';

import {useSubmitFee} from './use-submit-fee';
import {useSubmitTuition} from './use-submit-tuition';
import type * as Types from '../types';
import {transformApiProductToFee, transformApiSessionToTuition} from '../transformers/api-transformers';
import {transformTuitionOrFeeToTuitionAndFeeOption} from '../transformers/select-options-transformers';
import {transformNewFeeToApiProduct, transformNewTuitionToAPISessionPackage} from '../add-new-item/transformers';
import * as Tracking from '../tracking';
import * as Helpers from '../helpers/helpers';

type Args = {
    billingProfiles: Array<Types.BillingProfile>;
    handleAllTuitionsUpdate: Types.HandleAllTuitionsUpdate;
    handleAllFeesUpdate: Types.HandleAllFeesUpdate;
    handleModalClose: Types.HandleModalClose;
    pricingGroupState: Types.State['pricingGroupState'];
    displayedModal: Types.Modal;
    siteId: string;
    allChildren: Array<Types.Child>;
};

type Result = {
    handleNewItemSubmit: Types.HandleNewItemSubmit;
    isSessionPackageCreationLoading: boolean;
    isProductCreationLoading: boolean;
};

export const useSubmitNewItem = ({
    billingProfiles,
    handleAllTuitionsUpdate,
    handleAllFeesUpdate,
    handleModalClose,
    pricingGroupState: {selectedPricingGroup, monthlyPeriodPricingGroups, weeklyPeriodPricingGroups},
    displayedModal,
    siteId,
    allChildren,
}: Args): Result => {
    const {submit: submitTuition, isSessionPackageCreationLoading} = useSubmitTuition();
    const {submit: submitFee, isProductCreationLoading} = useSubmitFee();

    const afterNewTuitionSubmission: Types.AfterNewTuitionSubmission = useCallback(
        createdTuition => {
            const tuitionWithWarning = transformApiSessionToTuition({
                billingProfiles,
                hiddenTuitionsToDisplay: [createdTuition.createSessionPackage.session.id],
            })(createdTuition.createSessionPackage.session);

            const tuition = Warnable.dispatchWarnings(tuitionWithWarning, warnings => {
                SentryService.captureMessage(
                    `Tuitions submission warnings: ${warnings.map(w => w.warning).join(', ')}`,
                );
            });

            if (!tuition) {
                GlobalEvent.updateError(i18next.t('somethingWentWrong'));
                return;
            }

            handleAllTuitionsUpdate(tuition);

            const newTuitionOption = transformTuitionOrFeeToTuitionAndFeeOption({
                tuitionOrFee: tuition,
                selectedPricingGroup,
                monthlyPeriodPricingGroups,
                weeklyPeriodPricingGroups,
            });

            if (!newTuitionOption) {
                handleModalClose();
                return;
            }

            if (displayedModal.type === 'addTuition') {
                displayedModal.payload.selectItem(newTuitionOption);
            }
            handleModalClose();
        },
        [
            billingProfiles,
            handleAllTuitionsUpdate,
            selectedPricingGroup,
            monthlyPeriodPricingGroups,
            weeklyPeriodPricingGroups,
            displayedModal,
            handleModalClose,
        ],
    );

    const afterNewFeeSubmission: Types.AfterNewFeeSubmission = useCallback(
        createdFee => {
            const fee = transformApiProductToFee({hiddenFeesToDisplay: [createdFee.createProduct.product.id]})(
                createdFee.createProduct.product,
            );

            if (!fee) {
                GlobalEvent.updateError(i18next.t('somethingWentWrong'));
                return;
            }

            handleAllFeesUpdate(fee);

            const newFeeOption = transformTuitionOrFeeToTuitionAndFeeOption({
                tuitionOrFee: fee,
                selectedPricingGroup,
                monthlyPeriodPricingGroups,
                weeklyPeriodPricingGroups,
            });

            if (!newFeeOption) {
                handleModalClose();
                return;
            }

            if (displayedModal.type === 'addFee') {
                displayedModal.payload.selectItem(newFeeOption);
            }

            handleModalClose();
        },
        [
            displayedModal,
            monthlyPeriodPricingGroups,
            weeklyPeriodPricingGroups,
            handleAllFeesUpdate,
            handleModalClose,
            selectedPricingGroup,
        ],
    );

    const handleNewItemSubmit: Types.HandleNewItemSubmit = useCallback(
        newItem => {
            switch (newItem.type) {
                case 'tuition':
                    {
                        const session = transformNewTuitionToAPISessionPackage(newItem, siteId);
                        Tracking.newPlanAddNewSaved({
                            type: 'tuition',
                            addToLibrary: !session.hiddenByUser,
                            allPricingGroups: Helpers.hasAllPricingGroups({type: 'tuition', ...session}, allChildren),
                        });

                        submitTuition(session, afterNewTuitionSubmission, true);
                    }
                    break;

                case 'fee':
                    if (newItem.type === 'fee') {
                        const product = transformNewFeeToApiProduct(newItem, siteId);
                        Tracking.newPlanAddNewSaved({
                            type: 'fee',
                            addToLibrary: !product.hiddenByUser,
                            allPricingGroups: Helpers.hasAllPricingGroups({type: 'fee', ...product}, allChildren),
                        });
                        submitFee(product, afterNewFeeSubmission, true);
                    }
                    break;

                default:
                    GlobalEvent.updateError(i18next.t('somethingWentWrong'));
                    break;
            }
        },
        [afterNewFeeSubmission, afterNewTuitionSubmission, allChildren, siteId, submitFee, submitTuition],
    );

    return {handleNewItemSubmit, isSessionPackageCreationLoading, isProductCreationLoading};
};
